import { Container, Text } from '@prismeet/react'
import { useEffect, useState, type AnimationEvent, type ComponentType } from 'react'

import {
  BellIcon,
  CompassIcon,
  MessageIcon,
  SparkleIcon,
  UserIcon,
  type IconProps,
} from '@/shared/icons'

import {
  AUTH_PUSHES,
  MAX_VISIBLE_PUSHES,
  PUSH_FIRST_DELAY_MS,
  PUSH_INTERVAL_MS,
  PUSH_MEDIA_QUERY,
  type AuthPush,
} from './constants'
import styles from './index.module.scss'

const PUSH_ICONS: Record<string, ComponentType<IconProps>> = {
  feed: CompassIcon,
  mira: UserIcon,
  voice: MessageIcon,
  kostya: BellIcon,
  spaces: SparkleIcon,
  product: MessageIcon,
}

/**
 * Точки вокруг формы. Порядок чередует стороны: два соседних пуша не должны
 * выходить рядом, иначе витрина сбивается в один угол.
 *
 * Пять точек на шесть текстов — пары «текст ↔ точка» смещаются каждый круг,
 * и повтор не бросается в глаза. Живых пушей не больше трёх, значит точка
 * освобождается задолго до того, как очередь дойдёт до неё снова.
 */
const PUSH_SLOTS = [
  styles.slotLeftTop,
  styles.slotRightMid,
  styles.slotLeftBottom,
  styles.slotRightTop,
  styles.slotLeftMid,
]

interface VisiblePush {
  /** Монотонный счётчик: id повторяется на каждом круге, ключом быть не может. */
  key: number
  push: AuthPush
}

/**
 * Поток пушей на экранах входа и регистрации.
 *
 * Тезисы о продукте приезжают поверх фона, а не стоят колонкой сбоку:
 * колонку видели только широкие экраны, и на телефоне витрина пропадала
 * целиком. Здесь она хотя бы честно отсутствует, а не отсутствует молча.
 *
 * Блок декоративный: `aria-hidden` и `pointer-events: none`. Диктору нечего
 * зачитывать поверх формы, а курсор должен попадать в поля сквозь пуши.
 */
export function FeaturePush() {
  const [visible, setVisible] = useState<VisiblePush[]>([])
  const [wideEnough, setWideEnough] = useState(() => window.matchMedia(PUSH_MEDIA_QUERY).matches)

  useEffect(() => {
    const media = window.matchMedia(PUSH_MEDIA_QUERY)

    const sync = () => {
      setWideEnough(media.matches)

      // Окно сузили — очередь сбрасываем здесь же: иначе при возврате на
      // широкий экран накопленное вывалится тремя карточками разом.
      if (!media.matches) setVisible([])
    }

    media.addEventListener('change', sync)

    return () => {
      media.removeEventListener('change', sync)
    }
  }, [])

  useEffect(() => {
    if (!wideEnough) return

    let next = 0
    // Цепочка таймаутов, а не интервал: первый пуш приходит раньше
    // остальных, и одним интервалом такую паузу не задать.
    let timer = 0

    const spawn = () => {
      timer = window.setTimeout(spawn, PUSH_INTERVAL_MS)

      // В фоновой вкладке CSS-анимации стоят, а значит пуши не уезжают
      // и `onAnimationEnd` не приходит. Не копим то, что никто не видит.
      if (document.hidden) return

      const push = AUTH_PUSHES[next % AUTH_PUSHES.length]
      if (!push) return

      const key = next
      next += 1

      setVisible((current) => [...current, { key, push }].slice(-MAX_VISIBLE_PUSHES))
    }

    timer = window.setTimeout(spawn, PUSH_FIRST_DELAY_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [wideEnough])

  // Уход по концу анимации, а не по второму таймеру: время жизни задано
  // в стилях одним значением, и рассинхронизировать его неоткуда.
  const handleAnimationEnd = (key: number) => (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return

    setVisible((current) => current.filter((item) => item.key !== key))
  }

  if (!wideEnough) return null

  return (
    <div className={styles.pushes} aria-hidden="true">
      {visible.map(({ key, push }) => {
        const Icon = PUSH_ICONS[push.id] ?? SparkleIcon
        const slot = PUSH_SLOTS[key % PUSH_SLOTS.length]

        return (
          // Обёртка держит точку и цикл появления, карточка внутри — качание:
          // на одном элементе две анимации спорили бы за `transform`.
          <div
            key={key}
            className={`${styles.slot} ${slot ?? ''}`}
            onAnimationEnd={handleAnimationEnd(key)}
          >
            <Container
              surface="glass"
              glassLevel={2}
              dispersion
              radius="2xl"
              padding={4}
              display="flex"
              gap={3}
              align="start"
              className={styles.push}
            >
              <span className={styles.pushIcon}>
                <Icon />
              </span>
              <span className={styles.pushText}>
                <Text variant="label" weight="semibold">
                  {push.title}
                </Text>
                {'description' in push ? (
                  <Text variant="caption" tone="tertiary">
                    {push.description}
                  </Text>
                ) : null}
              </span>
            </Container>
          </div>
        )
      })}
    </div>
  )
}
